import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/serviceMedia";
import { mediaPreviewUrl, mediaPublicUrl } from "@/lib/mediaUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function toUrl(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.src || "";
}

function fromUrl(url, mode, alt = "") {
  if (mode === "media") {
    return url ? { type: "image", src: url, alt: alt || "" } : null;
  }
  return url || "";
}

/**
 * Shared image field for add/edit drawers.
 * Primary actions: Upload + Choose from library. URL paste is advanced-only.
 *
 * @param {'url'|'media'} valueMode - 'url' stores a string; 'media' stores {type,src,alt}
 */
export default function MediaImageField({
  value,
  onChange,
  folder = "uploads",
  valueMode = "url",
  label,
  hint,
  aspectClass = "h-40",
  fallbackSrc = "",
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const fileRef = useRef(null);
  const url = toUrl(value);
  const displayUrl = resolveMediaUrl(url, fallbackSrc) || fallbackSrc;
  const alt = valueMode === "media" ? (value?.alt || "") : "";

  const emit = (nextUrl, nextAlt = alt) => {
    onChange(fromUrl(nextUrl, valueMode, nextAlt));
  };

  const useStaticFallback = () => {
    if (!fallbackSrc) return;
    emit(fallbackSrc);
    toast.success("Using website static image");
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);
      const { data } = await api.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      emit(mediaPublicUrl(data));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      {label && <Label className="text-xs uppercase tracking-wider text-white/50">{label}</Label>}
      {hint && <p className="text-[11px] text-white/40">{hint}</p>}

      {displayUrl ? (
        <div className={`relative group rounded-lg overflow-hidden border border-white/10 ${aspectClass}`}>
          <img
            src={displayUrl}
            alt={alt || "Preview"}
            className="w-full h-full object-cover bg-black/40"
            onError={(e) => {
              if (fallbackSrc && e.currentTarget.src !== fallbackSrc) {
                e.currentTarget.src = fallbackSrc;
                return;
              }
              e.currentTarget.style.opacity = "0.3";
            }}
          />
          {url && url.includes("/api/media/public/") && fallbackSrc ? (
            <span className="absolute bottom-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/90 text-black">
              Previewing website fallback
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => emit("")}
            className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Remove image"
          >
            <Icons.X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : null}

      {valueMode === "media" && url ? (
        <Input
          value={alt}
          onChange={(e) => emit(url, e.target.value)}
          placeholder="Alt text (accessibility)"
          className="bg-black/20 border-white/10 text-white text-xs h-8"
        />
      ) : null}

      <div
        className={`flex flex-col gap-2 p-3 rounded-lg border border-dashed transition-colors ${
          dragOver ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/20"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          upload(e.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            className="btn-gradient text-white border-0 h-8"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            <Icons.Upload className="w-3.5 h-3.5 mr-1.5" />
            {uploading ? "Uploading…" : "Upload image"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-white/80 h-8 border border-white/10"
            onClick={() => setLibraryOpen(true)}
          >
            <Icons.FolderOpen className="w-3.5 h-3.5 mr-1.5" />
            Choose from library
          </Button>
          {fallbackSrc ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-white/80 h-8 border border-white/10"
              onClick={useStaticFallback}
            >
              <Icons.Image className="w-3.5 h-3.5 mr-1.5" />
              Use website image
            </Button>
          ) : null}
        </div>
        <p className="text-[11px] text-white/40">Drop an image here, or pick from the media library.</p>
      </div>

      <button
        type="button"
        className="text-[11px] text-white/40 hover:text-white/70"
        onClick={() => setShowUrl((v) => !v)}
      >
        {showUrl ? "Hide URL field" : "Advanced: paste image URL"}
      </button>
      {showUrl && (
        <Input
          value={url}
          onChange={(e) => emit(e.target.value)}
          placeholder="https://… or /api/media/public/…"
          className="bg-black/20 border-white/10 text-white placeholder:text-white/30 text-xs"
        />
      )}

      <MediaLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        onSelect={(selectedUrl) => {
          emit(selectedUrl);
          setLibraryOpen(false);
          toast.success("Image selected");
        }}
      />
    </div>
  );
}

function LibraryThumb({ item }) {
  const [broken, setBroken] = useState(false);
  const src = mediaPreviewUrl(item);
  if (broken) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black/50 text-amber-200/80">
        <Icons.ImageOff className="w-6 h-6" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={item.alt_text || ""}
      className="w-full h-full object-cover bg-black/40"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

function MediaLibraryDialog({ open, onOpenChange, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/media", { params: { type: "image", page_size: 60, folder: "all" } })
      .then((r) => {
        if (!cancelled) setItems(r.data.items || r.data || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = items.filter((it) => {
    if (!q.trim()) return true;
    const hay = `${it.original_filename || ""} ${it.alt_text || ""} ${it.folder || ""}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 text-white sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Media library</DialogTitle>
          <DialogDescription className="text-white/50">
            Pick an existing image, or upload new files from the Media page.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by filename…"
          className="bg-black/20 border-white/10 text-white"
        />
        <div className="flex-1 overflow-y-auto min-h-[240px]">
          {loading ? (
            <p className="text-sm text-white/40 p-4">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-white/40 p-4">No images found. Open Media Library and use “Import website images”.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-1">
              {filtered.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onSelect(mediaPublicUrl(it))}
                  className="aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                  title={it.original_filename || it.id}
                >
                  <LibraryThumb item={it} />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
